import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { FeatureGate, UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ListViewHeader } from '../components/ui/list-view-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Eye, 
  Edit, 
  Copy, 
  Send, 
  Trash2, 
  FileCheck,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRightCircle,
  DollarSign,
  TrendingUp,
  FileText,
  Crown,
  LayoutTemplate,
  BarChart3,
  Users,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { format, parseISO, isAfter, subDays } from 'date-fns';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-500', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-orange-500', icon: AlertCircle },
  converted: { label: 'Converted', color: 'bg-[#ffd108] text-black', icon: ArrowRightCircle }
};

const CHART_COLORS = ['#ffd108', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B'];

const Quotes = () => {
  const { api } = useAuth();
  const { hasFeature } = useTier();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('quotes');
  
  // Quotes state
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Templates state (Professional)
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    default_terms: '',
    default_validity_days: 30,
    is_default: false
  });
  
  // Analytics state (Professional)
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDates, setAnalyticsDates] = useState({
    start_date: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });

  const hasProfessionalFeatures = hasFeature('quote_templates');

  const fetchQuotes = async () => {
    try {
      const [quotesRes, statsRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/quotes/stats/summary')
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    if (!hasProfessionalFeatures) return;
    setTemplatesLoading(true);
    try {
      const response = await api.get('/quotes/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!hasProfessionalFeatures) return;
    setAnalyticsLoading(true);
    try {
      const response = await api.get('/quotes/analytics/dashboard', {
        params: analyticsDates
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [api]);

  useEffect(() => {
    if (activeTab === 'templates' && hasProfessionalFeatures) {
      fetchTemplates();
    } else if (activeTab === 'analytics' && hasProfessionalFeatures) {
      fetchAnalytics();
    }
  }, [activeTab, hasProfessionalFeatures]);

  const handleDuplicate = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/duplicate`);
      toast.success('Quote duplicated');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to duplicate quote');
    }
  };

  const handleCreateRevision = async (quoteId) => {
    try {
      const response = await api.post(`/quotes/${quoteId}/create-revision`);
      toast.success('New revision created');
      navigate(`/quotes/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    }
  };

  const handleSend = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/send`);
      toast.success('Quote sent');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!confirm('Are you sure you want to delete this quote?')) return;
    try {
      await api.delete(`/quotes/${quoteId}`);
      toast.success('Quote deleted');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name) {
      toast.error('Template name is required');
      return;
    }
    try {
      if (editingTemplate) {
        await api.put(`/quotes/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/quotes/templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', description: '', default_terms: '', default_validity_days: 30, is_default: false });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/quotes/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      default_terms: template.default_terms || '',
      default_validity_days: template.default_validity_days || 30,
      is_default: template.is_default || false
    });
    setShowTemplateDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const isExpiringSoon = (validUntil) => {
    if (!validUntil) return false;
    const expiryDate = parseISO(validUntil);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return isAfter(sevenDaysFromNow, expiryDate) && isAfter(expiryDate, new Date());
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Prepare chart data for analytics
  const statusPieData = analytics ? Object.entries(analytics.by_status || {}).map(([status, data], index) => ({
    name: STATUS_CONFIG[status]?.label || status,
    value: data.count,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })) : [];

  return (
    <FeatureGate 
      feature="quotes"
      title="Quotes"
      description="Create and manage quotes for your customers. Send quotes for approval and convert them to jobs."
    >
      <div className="space-y-6" data-testid="quotes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Quotes</h1>
            <p className="text-muted-foreground">Create, send, and manage customer quotes</p>
          </div>
          <div className="flex items-center gap-2">
            {hasProfessionalFeatures && (
              <Badge className="bg-purple-500 text-white">
                <Crown className="h-3 w-3 mr-1" />
                Professional
              </Badge>
            )}
            <Button 
              onClick={() => navigate('/quotes/new')} 
              className="btn-volt"
              data-testid="create-quote-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Quote
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="quotes" data-testid="quotes-tab">
              <FileCheck className="h-4 w-4 mr-2" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Templates
              {!hasProfessionalFeatures && <Crown className="h-3 w-3 ml-1 text-purple-500" />}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
              {!hasProfessionalFeatures && <Crown className="h-3 w-3 ml-1 text-purple-500" />}
            </TabsTrigger>
          </TabsList>

          {/* ==================== QUOTES TAB ==================== */}
          <TabsContent value="quotes" className="space-y-6">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-industrial">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <FileCheck className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Quotes</p>
                        <p className="text-2xl font-heading font-bold">{stats.total_quotes}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-industrial">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#ffd108]/20">
                        <DollarSign className="h-5 w-5 text-[#ffd108]" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Value</p>
                        <p className="text-2xl font-heading font-bold text-[#ffd108]">{formatCurrency(stats.total_value)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-industrial">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/20">
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Conversion Rate</p>
                        <p className="text-2xl font-heading font-bold text-green-500">{stats.conversion_rate}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-industrial">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-orange-500/20">
                        <Clock className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending</p>
                        <p className="text-2xl font-heading font-bold">
                          {(stats.by_status?.draft?.count || 0) + (stats.by_status?.sent?.count || 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Quotes List */}
            <Card className="card-industrial">
              <CardHeader className="pb-3">
                <ListViewHeader
                  count={filteredQuotes.length}
                  countLabel="Quotes"
                  showRefresh={false}
                  
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  searchPlaceholder="Search quotes..."
                  searchTestId="quote-search-input"
                  
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  statusTestId="status-filter"
                  statusOptions={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'sent', label: 'Sent' },
                    { value: 'viewed', label: 'Viewed' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'expired', label: 'Expired' },
                    { value: 'converted', label: 'Converted' },
                  ]}
                  
                  showDateFilter={false}
                />
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">Loading quotes...</div>
                ) : filteredQuotes.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-lg font-semibold mb-2">No quotes found</h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Try adjusting your search or filters'
                        : 'Create your first quote to get started'}
                    </p>
                    {!searchTerm && statusFilter === 'all' && (
                      <Button onClick={() => navigate('/quotes/new')} className="btn-volt">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Quote
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase tracking-wider">Quote #</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider">Valid Until</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredQuotes.map((quote) => (
                          <TableRow 
                            key={quote.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/quotes/${quote.id}`)}
                            data-testid={`quote-row-${quote.quote_number}`}
                          >
                            <TableCell className="font-mono font-medium">
                              {quote.quote_number}
                              {quote.version > 1 && (
                                <Badge variant="outline" className="ml-2 text-xs">v{quote.version}</Badge>
                              )}
                            </TableCell>
                            <TableCell>{quote.customer_name}</TableCell>
                            <TableCell>
                              <StatusBadge status={quote.status} />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm">
                                  {quote.valid_until ? format(parseISO(quote.valid_until), 'dd MMM yyyy') : '-'}
                                </span>
                                {isExpiringSoon(quote.valid_until) && quote.status === 'sent' && (
                                  <Badge variant="outline" className="text-orange-500 border-orange-500 text-xs">
                                    Expiring Soon
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                              {formatCurrency(quote.total)}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" data-testid={`quote-actions-${quote.quote_number}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${quote.id}`); }}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    View
                                  </DropdownMenuItem>
                                  {quote.status === 'draft' && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${quote.id}/edit`); }}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  {(quote.status === 'draft' || quote.status === 'sent') && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSend(quote.id); }}>
                                      <Send className="h-4 w-4 mr-2" />
                                      {quote.status === 'draft' ? 'Send' : 'Resend'}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(quote.id); }}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  {hasProfessionalFeatures && ['sent', 'viewed', 'rejected'].includes(quote.status) && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCreateRevision(quote.id); }}>
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Create Revision
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  {quote.status === 'draft' && (
                                    <DropdownMenuItem 
                                      onClick={(e) => { e.stopPropagation(); handleDelete(quote.id); }}
                                      className="text-red-500 focus:text-red-500"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== TEMPLATES TAB ==================== */}
          <TabsContent value="templates" className="space-y-6">
            {!hasProfessionalFeatures ? (
              <UpgradePrompt 
                feature="quote_templates"
                title="Quote Templates"
                description="Create reusable quote templates to speed up your quoting process. Save your common line items, terms, and pricing to use across multiple quotes."
              />
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-heading font-semibold">Quote Templates</h2>
                    <p className="text-muted-foreground text-sm">Create reusable templates for common quote types</p>
                  </div>
                  <Button 
                    onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', description: '', default_terms: '', default_validity_days: 30, is_default: false }); setShowTemplateDialog(true); }}
                    className="btn-volt"
                    data-testid="create-template-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>

                {templatesLoading ? (
                  <div className="p-8 text-center text-muted-foreground">Loading templates...</div>
                ) : templates.length === 0 ? (
                  <Card className="card-industrial border-dashed">
                    <CardContent className="py-12 text-center">
                      <LayoutTemplate className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="font-heading text-lg font-semibold mb-2">No templates yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create your first template to speed up quoting
                      </p>
                      <Button 
                        onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', description: '', default_terms: '', default_validity_days: 30, is_default: false }); setShowTemplateDialog(true); }}
                        className="btn-volt"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((template) => (
                      <Card key={template.id} className="card-industrial">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg flex items-center gap-2">
                                {template.name}
                                {template.is_default && (
                                  <Badge className="bg-[#ffd108] text-black text-xs">Default</Badge>
                                )}
                              </CardTitle>
                              {template.description && (
                                <CardDescription className="mt-1">{template.description}</CardDescription>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditTemplate(template)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTemplate(template.id)} className="text-red-500">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex justify-between">
                              <span>Validity:</span>
                              <span className="font-medium text-foreground">{template.default_validity_days} days</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Line Items:</span>
                              <span className="font-medium text-foreground">{template.default_line_items?.length || 0}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ==================== ANALYTICS TAB ==================== */}
          <TabsContent value="analytics" className="space-y-6">
            {!hasProfessionalFeatures ? (
              <UpgradePrompt 
                feature="quote_analytics"
                title="Quote Analytics"
                description="Get deep insights into your quoting performance. Track conversion rates, average quote values, time to approval, and identify your top customers."
              />
            ) : (
              <>
                {/* Date Range Filter */}
                <Card className="card-industrial">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row items-end gap-4">
                      <div className="space-y-2 flex-1">
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={analyticsDates.start_date}
                          onChange={(e) => setAnalyticsDates(prev => ({ ...prev, start_date: e.target.value }))}
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2 flex-1">
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={analyticsDates.end_date}
                          onChange={(e) => setAnalyticsDates(prev => ({ ...prev, end_date: e.target.value }))}
                          className="font-mono"
                        />
                      </div>
                      <Button onClick={fetchAnalytics} disabled={analyticsLoading} className="btn-volt">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        {analyticsLoading ? 'Loading...' : 'Generate'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {analytics && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Total Quotes</p>
                          <p className="text-2xl font-heading font-bold">{analytics.summary.total_quotes}</p>
                        </CardContent>
                      </Card>
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Total Value</p>
                          <p className="text-2xl font-heading font-bold text-[#ffd108]">{formatCurrency(analytics.summary.total_value)}</p>
                        </CardContent>
                      </Card>
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Avg Quote Value</p>
                          <p className="text-2xl font-heading font-bold">{formatCurrency(analytics.summary.avg_quote_value)}</p>
                        </CardContent>
                      </Card>
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Avg Approved Value</p>
                          <p className="text-2xl font-heading font-bold text-green-500">{formatCurrency(analytics.summary.avg_approved_value)}</p>
                        </CardContent>
                      </Card>
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Conversion Rate</p>
                          <p className="text-2xl font-heading font-bold text-green-500">{analytics.summary.conversion_rate}%</p>
                        </CardContent>
                      </Card>
                      <Card className="card-industrial">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground uppercase">Avg Days to Approve</p>
                          <p className="text-2xl font-heading font-bold">{analytics.summary.avg_time_to_approval_days}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Monthly Trend */}
                      {analytics.monthly_trend?.length > 0 && (
                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle>Monthly Trend</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="h-[300px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={analytics.monthly_trend}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                                  <Tooltip 
                                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                                  />
                                  <Line yAxisId="left" type="monotone" dataKey="count" stroke="#ffd108" strokeWidth={2} name="Quotes" />
                                  <Line yAxisId="right" type="monotone" dataKey="conversion_rate" stroke="#10B981" strokeWidth={2} name="Conversion %" />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Status Distribution */}
                      {statusPieData.length > 0 && (
                        <Card className="card-industrial">
                          <CardHeader>
                            <CardTitle>Status Distribution</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="h-[300px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={statusPieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                  >
                                    {statusPieData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {/* Top Customers */}
                    {analytics.top_customers?.length > 0 && (
                      <Card className="card-industrial">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Top Customers by Quote Value
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Customer</TableHead>
                                <TableHead className="text-right">Quotes</TableHead>
                                <TableHead className="text-right">Approved</TableHead>
                                <TableHead className="text-right">Total Value</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {analytics.top_customers.map((customer, index) => (
                                <TableRow key={customer.customer_id}>
                                  <TableCell className="font-medium">{customer.customer_name}</TableCell>
                                  <TableCell className="text-right font-mono">{customer.count}</TableCell>
                                  <TableCell className="text-right font-mono text-green-500">{customer.approved}</TableCell>
                                  <TableCell className="text-right font-mono font-semibold text-[#ffd108]">
                                    {formatCurrency(customer.value)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {!analytics && !analyticsLoading && (
                  <Card className="card-industrial border-dashed">
                    <CardContent className="py-12 text-center">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="font-heading text-lg font-semibold mb-2">Generate Analytics</h3>
                      <p className="text-muted-foreground">
                        Select a date range and click Generate to view your quote analytics
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Standard Excavator Quote"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this template"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Terms & Conditions</Label>
                <Textarea
                  value={templateForm.default_terms}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, default_terms: e.target.value }))}
                  placeholder="Default terms and conditions for quotes using this template"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Validity (days)</Label>
                  <Input
                    type="number"
                    value={templateForm.default_validity_days}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, default_validity_days: parseInt(e.target.value) || 30 }))}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Template</Label>
                  <div className="flex items-center h-10">
                    <input
                      type="checkbox"
                      checked={templateForm.is_default}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm text-muted-foreground">Set as default</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate} className="btn-volt">
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FeatureGate>
  );
};

export default Quotes;
